import { Injectable, computed, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  MixedSchema,
  initVersionedSchema,
  versionedSchemaToHtml,
  getSchemaSize,
} from '@principle-theorem/editor';
import { EditorPresetsService } from '@principle-theorem/ng-interactions';
import {
  OrganisationService,
  PdfService,
} from '@principle-theorem/ng-principle-shared';
import { FileManagerService } from '@principle-theorem/ng-shared';
import { Practice, Referral } from '@principle-theorem/principle-core';
import {
  IBrand,
  IClinicalNote,
  IDocumentSettings,
  IPatient,
  IReferral,
  IReferralMedia,
  IReferralPatientLetter,
  ReferralActor,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  WithRef,
  httpsCallable,
  serialise,
  snapshot,
} from '@principle-theorem/shared';
import { ReferralInteractionService } from './referral-interaction.service';
import { type AnyExtension } from '@tiptap/core';

export interface ISendPatientLetterOptions {
  brandRef: DocumentReference<IBrand>;
  referral: WithRef<IReferral>;
  patient: WithRef<IPatient>;
  currentUser: ReferralActor;
  subject: string;
  bodyContent: MixedSchema;
  emailBody?: MixedSchema;
  headerTemplate?: MixedSchema;
  footerTemplate?: MixedSchema;
  attachmentRefs?: DocumentReference<IReferralMedia>[];
  includeClinicalNotes: boolean;
  clinicalNotes?: WithRef<IClinicalNote>[];
}

@Injectable()
export class ReferralPatientLetterService {
  private _snackBar = inject(MatSnackBar);
  private _pdfService = inject(PdfService);
  private _editorPresets = inject(EditorPresetsService);
  private _fileManager = inject(FileManagerService);
  private _organisation = inject(OrganisationService);
  private _interactionService = inject(ReferralInteractionService);

  private _isSending = signal(false);
  private _isGeneratingPdf = signal(false);

  isSending = computed(() => this._isSending());
  isGeneratingPdf = computed(() => this._isGeneratingPdf());

  buildDefaultSubject(practiceName: string): string {
    return `Your Referral from ${practiceName}`;
  }

  buildDefaultEmailBody(
    patientName: string,
    practiceName: string
  ): MixedSchema {
    return initVersionedSchema(
      `Dear ${patientName},\n\nPlease find attached your referral letter from ${practiceName}.\n\nIf you have any questions, please don't hesitate to contact us.`
    );
  }

  async sendPatientLetter(options: ISendPatientLetterOptions): Promise<void> {
    const {
      brandRef,
      referral,
      patient,
      currentUser,
      subject,
      bodyContent,
      emailBody,
      headerTemplate,
      footerTemplate,
      attachmentRefs,
      includeClinicalNotes,
      clinicalNotes,
    } = options;

    if (!patient.email) {
      this._snackBar.open('Patient does not have an email address');
      return;
    }

    this._isSending.set(true);

    try {
      const extensions = this._editorPresets.defaultToHTMLExtensions();
      const bodyHtml = versionedSchemaToHtml(bodyContent, extensions);
      const headerHtml = headerTemplate
        ? versionedSchemaToHtml(headerTemplate, extensions)
        : undefined;
      const footerHtml = footerTemplate
        ? versionedSchemaToHtml(footerTemplate, extensions)
        : undefined;

      const emailBodyHtml = emailBody
        ? versionedSchemaToHtml(emailBody, extensions)
        : undefined;

      let clinicalNotesHtml: string | undefined;
      if (includeClinicalNotes && clinicalNotes && clinicalNotes.length > 0) {
        clinicalNotesHtml = this._buildClinicalNotesHtml(
          clinicalNotes,
          extensions
        );
      }

      await snapshot(
        httpsCallable('http-referral-sendPatientReferralLetter')(
          serialise({
            brandRef,
            referralRef: referral.ref,
            subject,
            bodyHtml,
            emailBody: emailBodyHtml,
            headerHtml,
            footerHtml,
            attachmentRefs,
            includeClinicalNotes,
            clinicalNotesHtml,
          })
        )
      );

      const referralStaffer = Referral.getStaffer(referral);
      if (referralStaffer) {
        await this._interactionService.createEmailSentToPatientInteraction(
          referral,
          referralStaffer,
          currentUser
        );
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          'No staffer actor found on referral; email-sent interaction not recorded.',
          referral.ref.path
        );
      }

      this._snackBar.open('Referral letter sent to patient');
    } catch (error) {
      this._snackBar.open('Error sending referral letter to patient');
      // eslint-disable-next-line no-console
      console.error('Error sending referral letter:', error);
      throw error;
    } finally {
      this._isSending.set(false);
    }
  }

  async previewPdf(
    letter: IReferralPatientLetter,
    clinicalNotes: WithRef<IClinicalNote>[]
  ): Promise<void> {
    this._isGeneratingPdf.set(true);

    try {
      const extensions = this._editorPresets.defaultToHTMLExtensions();
      const bodyHtml = this._buildBodyHtml(letter, clinicalNotes, extensions);
      const headerHtml = letter.headerTemplate
        ? versionedSchemaToHtml(letter.headerTemplate, extensions)
        : undefined;
      const footerHtml = letter.footerTemplate
        ? versionedSchemaToHtml(letter.footerTemplate, extensions)
        : undefined;

      const documentSettings = await this._resolveDocumentSettings();
      const blob = await this._pdfService.generateBlobFromHtml(
        bodyHtml,
        headerHtml,
        footerHtml,
        documentSettings
      );
      this._pdfService.printBlob(blob);
    } catch (error) {
      this._snackBar.open('Error generating PDF preview');
      // eslint-disable-next-line no-console
      console.error('Error generating PDF preview:', error);
    } finally {
      this._isGeneratingPdf.set(false);
    }
  }

  async downloadPdf(
    letter: IReferralPatientLetter,
    patientName: string,
    clinicalNotes: WithRef<IClinicalNote>[],
    attachments: WithRef<IReferralMedia>[]
  ): Promise<void> {
    this._isGeneratingPdf.set(true);

    try {
      const extensions = this._editorPresets.defaultToHTMLExtensions();
      const bodyHtml = this._buildBodyHtml(letter, clinicalNotes, extensions);
      const headerHtml = letter.headerTemplate
        ? versionedSchemaToHtml(letter.headerTemplate, extensions)
        : undefined;
      const footerHtml = letter.footerTemplate
        ? versionedSchemaToHtml(letter.footerTemplate, extensions)
        : undefined;

      const documentSettings = await this._resolveDocumentSettings();
      const blob = await this._pdfService.generateBlobFromHtml(
        bodyHtml,
        headerHtml,
        footerHtml,
        documentSettings
      );
      this._pdfService.downloadBlob(blob, patientName);

      if (attachments.length > 0) {
        await this._fileManager.downloadFiles('attachments', attachments);
      }
    } catch (error) {
      this._snackBar.open('Error downloading PDF');
      // eslint-disable-next-line no-console
      console.error('Error downloading PDF:', error);
    } finally {
      this._isGeneratingPdf.set(false);
    }
  }

  private async _resolveDocumentSettings(): Promise<IDocumentSettings> {
    const practice = await snapshot(this._organisation.practice$);
    return Practice.resolveDocumentSettings(practice);
  }

  private _buildBodyHtml(
    letter: IReferralPatientLetter,
    clinicalNotes: WithRef<IClinicalNote>[],
    extensions: AnyExtension[]
  ): string {
    let bodyHtml = versionedSchemaToHtml(letter.content, extensions);

    if (letter.includeClinicalNotes && clinicalNotes.length > 0) {
      bodyHtml += this._buildClinicalNotesHtml(clinicalNotes, extensions);
    }

    return bodyHtml;
  }

  private _buildClinicalNotesHtml(
    clinicalNotes: WithRef<IClinicalNote>[],
    extensions: AnyExtension[]
  ): string {
    const notesHtml = clinicalNotes
      .map((note) => {
        const noteContent = note.content;
        const schemaSize = noteContent ? getSchemaSize(noteContent) : 0;
        const noteHtml =
          noteContent && schemaSize > 0
            ? versionedSchemaToHtml(noteContent, extensions)
            : '<p style="color: #9ca3af; font-style: italic;">No content</p>';

        const recordDate = note.recordDate ?? 'Unknown date';
        const ownerName = note.owner?.name ?? 'Unknown';
        return `
            <div style="margin-bottom: 16px; padding: 12px; background: #f9fafb; border-radius: 8px;">
              <div style="font-size: 12px; color: #6b7280; margin-bottom: 8px;">
                ${recordDate} - ${ownerName}
              </div>
              <div>${noteHtml}</div>
            </div>
          `;
      })
      .join('');

    return `
        <div style="margin-top: 24px; border-top: 1px solid #e5e7eb; padding-top: 16px;">
          <h3 style="font-size: 14px; font-weight: 600; margin-bottom: 12px;">Clinical Notes</h3>
          ${notesHtml}
        </div>
      `;
  }
}
